import { useAtom, useSetAtom } from "jotai"
import clsx from "clsx"
import { useCallback, useEffect, useRef, useState } from "react"
import { Icon } from "@/components/icons/Icon"
import { ScrollBar } from "@/components/ui/ScrollBar"
import { ControlGroup } from "@/components/ControlGroup"
import { Button } from "@/components/ui/Button"
import { noopSliderTransform, Slider } from "@/components/ui/Slider"
import { PeopleDetectionLoadingPanel } from "@/components/People/PeopleDetectionLoadingPanel"
import {
	createMaskGroup,
	createPeopleMasksForSelection,
	transformMask,
} from "@/store/editor/mask-utils"
import { rectToNoOrientation } from "@/lib/ai/alignment/ai-image-align"
import type {
	PeopleMaskPartAvailability,
	VisiblePeopleSegmentationResult,
} from "@/io/assets/ai-mask/ai-mask-people-provider"
import { MainAIMaskResolver } from "@/io/assets/ai-mask/main-ai-mask-resolver"
import { useEditor } from "@/utils/useEditor"
import { useEditorPhysicalTransformKey } from "@/components/Masking/useEditorPhysicalTransformKey"
import type { MaskGroup, PeopleMaskPart } from "@/core/types"
import type { Rect } from "@/lib/ai/detection/face-detection"
import { detectMaskSequentially, showNoDetected } from "@/store/editor/masks-state"
import { saveCurrentAdjustmentState } from "@/store/store-history"
import {
	hoveredPeopleFaceIndexAtom,
	peopleFaceBoxesAtom,
	selectedPeopleFaceIndexAtom,
	visiblePeopleFaceIndexesAtom,
} from "@/store/editor/people-face-boxes"
import { clamp } from "@/utils/math"
import { useTranslation } from "react-i18next"
import {
	retouchingSectionTitleKeys,
	retouchingControlKeys,
	skinToneSwatchKeys,
} from "@/i18n/dual-role-strings"

type RetouchingSliderConfig = {
	title: string
	description?: string
	targetParts: PeopleMaskPart[]
	input?: {
		type: "stepped"
		min: number
		max: number
		step?: number
	}
}

type RetouchingSectionConfig = {
	title: string
	sliders: RetouchingSliderConfig[]
}

type PeopleAnalysisStatus = "idle" | "loading" | "ready" | "failed"

// i18n-ignore-start: dual-role data — titles stored in retouching.control, translated via retouchingControlKeys
const retouchingSections: RetouchingSectionConfig[] = [
	{
		title: "Skin Retouching",
		sliders: [
			{
				title: "Smoothing",
				description: "Skin Smoothing",
				targetParts: ["facialSkin", "bodySkin"],
			},
			{
				title: "Enhancement",
				description: "Skin Enhancement",
				targetParts: ["facialSkin", "bodySkin"],
			},
			{
				title: "Sculpt",
				description: "Skin Sculpt",
				targetParts: ["facialSkin", "bodySkin"],
			},
			{
				title: "Unify Tone",
				description: "Skin Unify Tone",
				targetParts: ["facialSkin", "bodySkin"],
			},
		],
	},
	{
		title: "Blemish Removal",
		sliders: [
			{ title: "Acne", description: "Acne Removal", targetParts: ["facialSkin"] },
			{ title: "Freckles", description: "Freckle Removal", targetParts: ["facialSkin"] },
			{ title: "Face Shine", description: "Face Shine Removal", targetParts: ["facialSkin"] },
			{ title: "Smile Line", description: "Smile Line Removal", targetParts: ["facialSkin"] },
			{
				title: "Dark Circles",
				description: "Dark Circle Removal",
				targetParts: ["facialSkin"],
			},
			{ title: "Eye Bags", description: "Eye Bag Removal", targetParts: ["eyes"] },
			{ title: "Eye Wrinkles", description: "Eye Wrinkle Removal", targetParts: ["eyes"] },
			{ title: "Stray Hair", description: "Stray Hair Removal", targetParts: ["hair"] },
		],
	},
	{
		title: "Teeth",
		sliders: [
			{ title: "Flaw Removal", description: "Teeth Flaw Removal", targetParts: ["lips"] },
			{ title: "Whitening", description: "Teeth Whitening", targetParts: ["lips"] },
			{
				title: "Alignment",
				description: "Teeth Alignment",
				targetParts: ["lips"],
				input: {
					type: "stepped",
					min: 0,
					max: 5,
				},
			},
		],
	},
	{
		title: "Eyes",
		sliders: [
			{ title: "Texture", description: "Eye Texture", targetParts: ["eyes"] },
			{ title: "Red Eye", description: "Red Eye Removal", targetParts: ["eyes"] },
			{ title: "Glasses Glare", description: "Glasses Glare Removal", targetParts: ["eyes"] },
		],
	},
]
// i18n-ignore-end

const getRetouchingSliderDescription = (
	section: RetouchingSectionConfig,
	slider: RetouchingSliderConfig,
) => slider.description ?? `${section.title} - ${slider.title}`

const getRetouchMaskKey = (
	personIndex: number,
	peoplePart: PeopleMaskPart,
	retouchingControl: string,
) => `${personIndex}:${peoplePart}:${retouchingControl}`

const getTemporaryExposure = (displayedValue: number) => displayedValue / 100
const getDisplayedExposure = (exposure: number) => Math.round(exposure * 100)

const getAvailableTargetParts = (
	slider: RetouchingSliderConfig,
	availability: PeopleMaskPartAvailability | null | undefined,
) => slider.targetParts.filter((targetPart) => availability?.[targetPart])

const getPeopleFaceBoxRect = (rect: Rect, initialOrientation: number): Rect => {
	const sourceRect = rectToNoOrientation(rect, initialOrientation)
	const x = clamp(sourceRect.x, 0, 1)
	const y = clamp(sourceRect.y, 0, 1)
	const right = clamp(sourceRect.x + sourceRect.width, 0, 1)
	const bottom = clamp(sourceRect.y + sourceRect.height, 0, 1)

	return {
		x,
		y,
		width: Math.max(0, right - x),
		height: Math.max(0, bottom - y),
	}
}

const skinToneSwatches = [
	{ id: "off", label: "No skin tone", color: "transparent" },
	{ id: "rose", label: "Rose", color: "#c77d73" },
	{ id: "mocha", label: "Mocha", color: "#8f5f53" },
	{ id: "copper", label: "Copper", color: "#9d5745" },
	{ id: "blush", label: "Blush", color: "#f0cbd0" },
	{ id: "cool", label: "Cool", color: "#d8eef4" },
	{ id: "peach", label: "Peach", color: "#f2d2ad" },
	{ id: "white", label: "White", color: "#f7f7f2" },
]

const initialVisibleFaceCount = 5
const expandedVisibleFaceCount = 15
const defaultSkinToneStrength = 50
const offSkinToneId = "off"
const skinToneRetouchingControl = "Skin Tone" // i18n-ignore: dual-role data key

export const PeoplePanel = () => {
	const { t } = useTranslation("components")
	const editor = useEditor()
	const setFaceBoxes = useSetAtom(peopleFaceBoxesAtom)
	const setVisiblePeopleFaceIndexes = useSetAtom(visiblePeopleFaceIndexesAtom)
	const [peopleAnalysis, setPeopleAnalysis] = useState<VisiblePeopleSegmentationResult | null>()
	const [peopleAnalysisStatus, setPeopleAnalysisStatus] = useState<PeopleAnalysisStatus>("idle")
	const [selectedPersonIndex, setSelectedPersonIndex] = useAtom(selectedPeopleFaceIndexAtom)
	const setHoveredPersonIndex = useSetAtom(hoveredPeopleFaceIndexAtom)
	const [maskPartAvailability, setMaskPartAvailability] = useState<
		Record<number, PeopleMaskPartAvailability | null>
	>({})
	const pendingRetouchMaskCreations = useRef(new Map<string, Promise<void>>())
	const transformKey = useEditorPhysicalTransformKey()
	const transformKeyRef = useRef(transformKey)

	useEffect(() => {
		transformKeyRef.current = transformKey
	}, [transformKey])

	useEffect(() => {
		setPeopleAnalysis(undefined)
		setPeopleAnalysisStatus("idle")
		setSelectedPersonIndex(null)
		setHoveredPersonIndex(null)
		setMaskPartAvailability({})
		pendingRetouchMaskCreations.current.clear()
		setFaceBoxes([])
		setVisiblePeopleFaceIndexes([])
	}, [
		setFaceBoxes,
		setHoveredPersonIndex,
		setSelectedPersonIndex,
		setVisiblePeopleFaceIndexes,
		transformKey,
	])

	useEffect(() => {
		if (!peopleAnalysis || peopleAnalysis.persons.length === 0) {
			setSelectedPersonIndex(null)
			setFaceBoxes([])
			setVisiblePeopleFaceIndexes([])
			return
		}

		setFaceBoxes(
			peopleAnalysis.persons.flatMap((person) => {
				// WIP: Face boxes only render when detection matched this person to a face.
				if (person.matchedFaceIndex === undefined) {
					return []
				}

				const face = peopleAnalysis.faces[person.matchedFaceIndex]
				if (!face) {
					return []
				}

				return [
					{
						personIndex: person.personIndex,
						rect: getPeopleFaceBoxRect(
							face.unPaddedRect ?? face.faceRect,
							editor.photo.initialOrientation,
						),
					},
				]
			}),
		)

		const firstPerson = peopleAnalysis.persons[0]
		if (firstPerson) {
			setSelectedPersonIndex(firstPerson.personIndex)
		}
		return () => {
			setFaceBoxes([])
			setVisiblePeopleFaceIndexes([])
		}
	}, [
		editor.photo.initialOrientation,
		peopleAnalysis,
		setFaceBoxes,
		setSelectedPersonIndex,
		setVisiblePeopleFaceIndexes,
	])

	const loadPeopleAnalysis = useCallback(async () => {
		if (peopleAnalysisStatus === "loading" || peopleAnalysisStatus === "ready") {
			return
		}

		setPeopleAnalysisStatus("loading")
		const photo = editor.photo
		const requestTransformKey = transformKey

		try {
			const result = await MainAIMaskResolver.people.getVisiblePeopleSegmentation(photo)
			if (editor.photo.id !== photo.id || transformKeyRef.current !== requestTransformKey) {
				return
			}
			setPeopleAnalysis(result)
			setPeopleAnalysisStatus("ready")
		} catch {
			if (editor.photo.id !== photo.id || transformKeyRef.current !== requestTransformKey) {
				return
			}
			setPeopleAnalysis(null)
			setPeopleAnalysisStatus("failed")
		}
	}, [editor.photo, peopleAnalysisStatus, transformKey])

	useEffect(() => {
		void loadPeopleAnalysis()
	}, [loadPeopleAnalysis])

	const selectedMaskPartAvailability =
		selectedPersonIndex === null ? undefined : maskPartAvailability[selectedPersonIndex]

	useEffect(() => {
		if (
			!peopleAnalysis ||
			selectedPersonIndex === null ||
			selectedMaskPartAvailability !== undefined
		) {
			return
		}

		const photo = editor.photo
		setMaskPartAvailability((current) => ({
			...current,
			[selectedPersonIndex]: null,
		}))

		void MainAIMaskResolver.people
			.getPeopleMaskPartAvailability(photo, [selectedPersonIndex])
			.then((availability) => {
				if (editor.photo.id !== photo.id) {
					return
				}
				setMaskPartAvailability((current) => ({
					...current,
					[selectedPersonIndex]: availability,
				}))
			})
			.catch(() => {
				if (editor.photo.id !== photo.id) {
					return
				}
				setMaskPartAvailability((current) => ({
					...current,
					[selectedPersonIndex]: null,
				}))
			})
	}, [editor.photo, peopleAnalysis, selectedMaskPartAvailability, selectedPersonIndex])

	const findRetouchingMaskGroupIndex = useCallback(
		(personIndex: number, peoplePart: PeopleMaskPart, retouchingControl: string) => {
			return (
				editor.adjustments.localAdjustments?.findIndex((group) => {
					if (
						group.masks.length !== 1 ||
						group.retouching?.control !== retouchingControl
					) {
						return false
					}

					const mask = group.masks[0]
					return (
						mask?.type === "people" &&
						mask.personIndex === personIndex &&
						(mask.peoplePart ?? "entire") === peoplePart
					)
				}) ?? -1
			)
		},
		[editor.adjustments.localAdjustments],
	)

	const getRetouchingMaskGroup = useCallback(
		(personIndex: number, peoplePart: PeopleMaskPart, retouchingControl: string) => {
			const groupIndex = findRetouchingMaskGroupIndex(
				personIndex,
				peoplePart,
				retouchingControl,
			)
			return groupIndex < 0 ? undefined : editor.adjustments.localAdjustments?.[groupIndex]
		},
		[editor.adjustments.localAdjustments, findRetouchingMaskGroupIndex],
	)

	const applyRetouchingMaskGroupProperties = useCallback(
		(
			personIndex: number,
			peoplePart: PeopleMaskPart,
			retouchingControl: string,
			properties: Partial<MaskGroup>,
		) => {
			const groupIndex = findRetouchingMaskGroupIndex(
				personIndex,
				peoplePart,
				retouchingControl,
			)
			if (groupIndex < 0) {
				return false
			}

			const localAdjustments = [...(editor.adjustments.localAdjustments ?? [])]
			const group = localAdjustments[groupIndex]
			if (!group) {
				return false
			}

			localAdjustments[groupIndex] = {
				...group,
				...properties,
				active: true,
			}
			editor.setAdjustments({ localAdjustments })
			return true
		},
		[editor, findRetouchingMaskGroupIndex],
	)

	const applyRetouchingExposure = useCallback(
		(
			personIndex: number,
			peoplePart: PeopleMaskPart,
			retouchingControl: string,
			exposure: number,
		) =>
			applyRetouchingMaskGroupProperties(personIndex, peoplePart, retouchingControl, {
				exposure,
			}),
		[applyRetouchingMaskGroupProperties],
	)

	const createRetouchingMaskGroups = useCallback(
		async (
			personIndex: number,
			peopleParts: PeopleMaskPart[],
			retouchingControl: string,
			properties: Partial<MaskGroup>,
		) => {
			const photo = editor.photo
			const masks = createPeopleMasksForSelection({
				selectedPersonIndexes: [personIndex],
				peopleParts,
				numberedNames: true,
				properties: { active: true },
			})
			if (masks.length === 0) {
				return
			}

			for (const mask of masks) {
				const detectedResult = MainAIMaskResolver.hasMaskDetected(photo, mask)
				if (detectedResult === false) {
					showNoDetected("people")
					return
				}
				if (detectedResult !== true) {
					const detectionResult = await detectMaskSequentially(photo, mask)
					if (!detectionResult) {
						showNoDetected("people")
						return
					}
				}
			}
			if (editor.photo.id !== photo.id) {
				return
			}

			const retouchingMaskGroups = masks.flatMap((mask) => {
				const peoplePart = mask.peoplePart ?? "entire"
				if (
					applyRetouchingMaskGroupProperties(
						personIndex,
						peoplePart,
						retouchingControl,
						properties,
					)
				) {
					return []
				}

				const transformedMask = transformMask(
					mask,
					editor.mainViewport.cropViewMatrixInv,
					editor.crop,
					editor.rotation,
				)
				return [
					createMaskGroup({
						name: `Retouch ${retouchingControl} ${mask.name}`,
						masks: [transformedMask],
						retouching: { control: retouchingControl },
						...properties,
						active: true,
					}),
				]
			})
			if (retouchingMaskGroups.length === 0) {
				return
			}

			editor.setAdjustments({
				localAdjustments: [
					...(editor.adjustments.localAdjustments ?? []),
					...retouchingMaskGroups,
				],
			})
		},
		[applyRetouchingMaskGroupProperties, editor],
	)

	const updateRetouchingSlider = useCallback(
		async (slider: RetouchingSliderConfig, displayedValue: number) => {
			if (selectedPersonIndex === null) {
				return
			}

			const availableTargetParts = getAvailableTargetParts(
				slider,
				maskPartAvailability[selectedPersonIndex],
			)
			if (availableTargetParts.length === 0) {
				return
			}

			const exposure = getTemporaryExposure(displayedValue)
			const missingTargetParts: PeopleMaskPart[] = []
			const pendingPromises: Promise<void>[] = []

			for (const targetPart of availableTargetParts) {
				if (
					applyRetouchingExposure(selectedPersonIndex, targetPart, slider.title, exposure)
				) {
					continue
				}

				const maskKey = getRetouchMaskKey(selectedPersonIndex, targetPart, slider.title)
				const existingPromise = pendingRetouchMaskCreations.current.get(maskKey)
				if (existingPromise) {
					pendingPromises.push(
						existingPromise.then(() => {
							applyRetouchingExposure(
								selectedPersonIndex,
								targetPart,
								slider.title,
								exposure,
							)
						}),
					)
					continue
				}

				missingTargetParts.push(targetPart)
			}

			if (missingTargetParts.length > 0) {
				const promise = createRetouchingMaskGroups(
					selectedPersonIndex,
					missingTargetParts,
					slider.title,
					{ exposure },
				).finally(() => {
					for (const targetPart of missingTargetParts) {
						pendingRetouchMaskCreations.current.delete(
							getRetouchMaskKey(selectedPersonIndex, targetPart, slider.title),
						)
					}
				})
				for (const targetPart of missingTargetParts) {
					pendingRetouchMaskCreations.current.set(
						getRetouchMaskKey(selectedPersonIndex, targetPart, slider.title),
						promise,
					)
				}
				pendingPromises.push(promise)
			}

			if (pendingPromises.length > 0) {
				await Promise.all(pendingPromises)
			}
		},
		[
			applyRetouchingExposure,
			createRetouchingMaskGroups,
			maskPartAvailability,
			selectedPersonIndex,
		],
	)

	const saveRetouchingSliderChange = useCallback(
		async (
			section: RetouchingSectionConfig,
			slider: RetouchingSliderConfig,
			displayedValue: number,
		) => {
			if (selectedPersonIndex === null) {
				return
			}
			if (
				getAvailableTargetParts(slider, maskPartAvailability[selectedPersonIndex])
					.length === 0
			) {
				return
			}

			await updateRetouchingSlider(slider, displayedValue)
			await saveCurrentAdjustmentState({
				message: "Retouching",
				changedValue: `${getRetouchingSliderDescription(section, slider)}: ${displayedValue}`,
			})
		},
		[maskPartAvailability, selectedPersonIndex, updateRetouchingSlider],
	)

	const getSliderValue = (slider: RetouchingSliderConfig) => {
		if (selectedPersonIndex === null) {
			return slider.input?.min ?? 0
		}

		const group = slider.targetParts
			.map((targetPart) =>
				getRetouchingMaskGroup(selectedPersonIndex, targetPart, slider.title),
			)
			.find(Boolean)

		const value = group ? getDisplayedExposure(group.exposure) : 0
		return slider.input ? clamp(value, slider.input.min, slider.input.max) : value
	}

	const getSkinToneGroup = () => {
		if (selectedPersonIndex === null) {
			return undefined
		}

		return getRetouchingMaskGroup(selectedPersonIndex, "entire", skinToneRetouchingControl)
	}

	const skinToneGroup = getSkinToneGroup()
	const selectedSkinTone = skinToneGroup?.retouching?.skinTone?.toneId ?? offSkinToneId
	const skinToneStrength = skinToneGroup
		? getDisplayedExposure(skinToneGroup.exposure)
		: defaultSkinToneStrength

	const updateSkinTone = useCallback(
		async (toneId: string, strength: number, historyValue?: string) => {
			if (selectedPersonIndex === null) {
				return
			}

			const properties = {
				retouching: {
					control: skinToneRetouchingControl,
					skinTone: {
						toneId,
						strength,
					},
				},
				exposure: getTemporaryExposure(strength),
			}

			if (
				applyRetouchingMaskGroupProperties(
					selectedPersonIndex,
					"entire",
					skinToneRetouchingControl,
					properties,
				)
			) {
				if (historyValue) {
					await saveCurrentAdjustmentState({
						message: "Retouching",
						changedValue: historyValue,
					})
				}
				return
			}
			if (toneId === offSkinToneId) {
				return
			}

			const maskKey = getRetouchMaskKey(
				selectedPersonIndex,
				"entire",
				skinToneRetouchingControl,
			)
			const existingPromise = pendingRetouchMaskCreations.current.get(maskKey)
			if (existingPromise) {
				await existingPromise
				if (
					applyRetouchingMaskGroupProperties(
						selectedPersonIndex,
						"entire",
						skinToneRetouchingControl,
						properties,
					) &&
					historyValue
				) {
					await saveCurrentAdjustmentState({
						message: "Retouching",
						changedValue: historyValue,
					})
				}
				return
			}

			const promise = createRetouchingMaskGroups(
				selectedPersonIndex,
				["entire"],
				skinToneRetouchingControl,
				properties,
			).finally(() => {
				pendingRetouchMaskCreations.current.delete(maskKey)
			})
			pendingRetouchMaskCreations.current.set(maskKey, promise)
			await promise
			if (historyValue) {
				await saveCurrentAdjustmentState({
					message: "Retouching",
					changedValue: historyValue,
				})
			}
		},
		[applyRetouchingMaskGroupProperties, createRetouchingMaskGroups, selectedPersonIndex],
	)

	const isSliderDisabled = (slider: RetouchingSliderConfig) =>
		selectedPersonIndex === null ||
		getAvailableTargetParts(slider, selectedMaskPartAvailability).length === 0

	const renderRetouchingSection = (section: RetouchingSectionConfig) => (
		<ControlGroup
			key={section.title}
			name={t(retouchingSectionTitleKeys[section.title] ?? section.title)}
		>
			{section.sliders.map((slider) => (
				<RetouchingSlider
					key={slider.title}
					title={t(retouchingControlKeys[slider.title] ?? slider.title)}
					value={getSliderValue(slider)}
					input={slider.input}
					disabled={isSliderDisabled(slider)}
					onUpdate={(value) => void updateRetouchingSlider(slider, value)}
					onUpdateEnded={(value) =>
						void saveRetouchingSliderChange(section, slider, value)
					}
				/>
			))}
		</ControlGroup>
	)

	return (
		<div
			aria-label={t("peoplePanel.peoplePanel")}
			className="scrollarea relative flex size-full flex-col overflow-y-auto bg-base-800 text-base-200"
		>
			<section className="px-4 py-5">
				<h2 className="mb-2 text-[15px] font-semibold leading-none text-base-100">
					{t("peoplePanel.faces")}
				</h2>
				{peopleAnalysisStatus === "loading" ? (
					<PeopleDetectionLoadingPanel className="min-h-20" />
				) : (
					<FaceSelector
						peopleAnalysis={peopleAnalysis}
						peopleAnalysisStatus={peopleAnalysisStatus}
						selectedPersonIndex={selectedPersonIndex}
						onSelectPerson={setSelectedPersonIndex}
						onHoverPerson={setHoveredPersonIndex}
					/>
				)}
			</section>

			{retouchingSections.slice(0, 1).map(renderRetouchingSection)}

			<ControlGroup name={t("peoplePanel.skinTone")}>
				<div className="mb-4 text-sm text-base-400">
					{t("peoplePanel.selectASkinToneTo")}
				</div>
				<div className="mb-4 flex items-center gap-2">
					{skinToneSwatches.map((swatch) => (
						<Button
							key={swatch.id}
							variant="custom"
							size="custom"
							trackingLabel="select_people_skin_tone"
							trackingValue={swatch.id}
							aria-label={t(skinToneSwatchKeys[swatch.label] ?? swatch.label)}
							className={clsx(
								"flex size-6 items-center justify-center rounded-full border outline-none transition-colors",
								selectedSkinTone === swatch.id
									? "border-accent-500"
									: "border-transparent hover:border-base-500",
							)}
							onClick={() =>
								void updateSkinTone(
									swatch.id,
									skinToneStrength,
									`Skin Tone: ${swatch.label}`,
								)
							}
						>
							<span
								className={clsx(
									"block size-5 rounded-full",
									swatch.id === offSkinToneId &&
										"border border-base-500 bg-base-700",
								)}
								style={{ backgroundColor: swatch.color }}
							>
								{swatch.id === offSkinToneId && (
									<span className="flex size-full items-center justify-center">
										<span className="h-px w-3 rotate-45 bg-base-500" />
									</span>
								)}
							</span>
						</Button>
					))}
				</div>
				<RetouchingSlider
					title={t("peoplePanel.strength")}
					value={skinToneStrength}
					disabled={selectedPersonIndex === null || selectedSkinTone === offSkinToneId}
					onUpdate={(value) => void updateSkinTone(selectedSkinTone, value)}
					onUpdateEnded={(value) =>
						void updateSkinTone(selectedSkinTone, value, `Skin Tone Strength: ${value}`)
					}
				/>
			</ControlGroup>

			{retouchingSections.slice(1).map(renderRetouchingSection)}
			<ScrollBar visible="hover" />
		</div>
	)
}

const FaceSelector = (props: {
	peopleAnalysis?: VisiblePeopleSegmentationResult | null
	peopleAnalysisStatus: PeopleAnalysisStatus
	selectedPersonIndex: number | null
	onSelectPerson: (personIndex: number) => void
	onHoverPerson: (personIndex: number | null) => void
}) => {
	const { t } = useTranslation("components")
	const [visibleFaceCount, setVisibleFaceCount] = useState(initialVisibleFaceCount)
	const setVisiblePeopleFaceIndexes = useSetAtom(visiblePeopleFaceIndexesAtom)

	useEffect(() => {
		setVisibleFaceCount(initialVisibleFaceCount)
	}, [props.peopleAnalysis])

	useEffect(() => {
		if (!props.peopleAnalysis || props.peopleAnalysis.persons.length === 0) {
			setVisiblePeopleFaceIndexes([])
			return
		}

		const persons = props.peopleAnalysis.persons
		const visiblePersons = persons.slice(0, visibleFaceCount)
		setVisiblePeopleFaceIndexes(visiblePersons.map((person) => person.personIndex))

		return () => {
			setVisiblePeopleFaceIndexes([])
		}
	}, [props.peopleAnalysis, setVisiblePeopleFaceIndexes, visibleFaceCount])

	if (props.peopleAnalysisStatus === "failed") {
		return (
			<div className="py-3 text-sm text-base-400">
				{t("peoplePanel.couldNotDetectPeople")}
			</div>
		)
	}

	if (props.peopleAnalysis === null) {
		return <div className="py-3 text-sm text-base-400">{t("peoplePanel.noPeopleDetected")}</div>
	}

	if (!props.peopleAnalysis || props.peopleAnalysis.persons.length === 0) {
		return <div className="h-[84px]" />
	}

	const persons = props.peopleAnalysis.persons
	const visiblePersons = persons.slice(0, visibleFaceCount)
	const nextPerson = persons[visibleFaceCount]
	const showMore = nextPerson !== undefined
	const handleShowMore = () => {
		setVisibleFaceCount((current) =>
			current === initialVisibleFaceCount
				? Math.min(expandedVisibleFaceCount, persons.length)
				: persons.length,
		)
	}

	return (
		<div
			className="grid grid-cols-4 gap-x-3 gap-y-4"
			onPointerDown={(e) => {
				e.stopPropagation()
			}}
			onWheel={(e) => {
				e.stopPropagation()
			}}
		>
			{visiblePersons.map((person) => {
				const label = `Person ${person.personIndex + 1}`
				const selected = props.selectedPersonIndex === person.personIndex
				return (
					<FaceSelectorTile
						key={person.personIndex}
						label={label}
						selected={selected}
						thumbnailUrl={person.thumbnailUrl}
						trackingLabel="select_people_face"
						trackingValue={person.personIndex.toString()}
						onClick={() => props.onSelectPerson(person.personIndex)}
						onHover={() => props.onHoverPerson(person.personIndex)}
						onBlur={() => props.onHoverPerson(null)}
					/>
				)
			})}
			{showMore && (
				<FaceSelectorTile
					label={t("peoplePanel.more")}
					selected={false}
					thumbnailUrl={nextPerson.thumbnailUrl}
					trackingLabel="show_more_people_faces"
					onClick={handleShowMore}
					onHover={() => props.onHoverPerson(nextPerson.personIndex)}
					onBlur={() => props.onHoverPerson(null)}
					tooltip={t("peoplePanel.showMoreFaces")}
					isMore
				/>
			)}
		</div>
	)
}

const FaceSelectorTile = (props: {
	label: string
	selected: boolean
	thumbnailUrl?: string
	trackingLabel: string
	trackingValue?: string
	tooltip?: string
	onClick: () => void
	onHover: () => void
	onBlur: () => void
	isMore?: boolean
}) => (
	<Button
		variant="custom"
		size="custom"
		trackingLabel={props.trackingLabel}
		trackingValue={props.trackingValue}
		tooltip={props.tooltip}
		className="flex min-w-0 flex-col items-center bg-transparent p-0 text-left outline-none hover:bg-transparent focus:bg-transparent active:bg-transparent"
		onClick={props.onClick}
		onPointerEnter={props.onHover}
		onPointerLeave={props.onBlur}
		onFocus={props.onHover}
		onBlur={props.onBlur}
	>
		<span
			className={clsx(
				"relative flex size-14 items-center justify-center overflow-hidden rounded-full bg-base-900 opacity-100 ring-accent-500 transition",
				props.selected && "ring-2",
			)}
		>
			{props.thumbnailUrl ? (
				<img
					src={props.thumbnailUrl}
					alt=""
					draggable={false}
					className={clsx(
						"size-full object-cover hover:opacity-50",
						props.isMore && "brightness-50",
					)}
				/>
			) : (
				<Icon name="people_mask" className="size-7 text-base-300" />
			)}
			{props.isMore && (
				<span className="absolute flex items-center gap-0.5">
					<span className="size-1.5 rounded-full bg-base-100" />
					<span className="size-1.5 rounded-full bg-base-100" />
					<span className="size-1.5 rounded-full bg-base-100" />
				</span>
			)}
		</span>
		<span className="w-full truncate text-center text-[11px] text-base-200">{props.label}</span>
	</Button>
)

const RetouchingSlider = (props: {
	title: string
	value: number
	input?: RetouchingSliderConfig["input"]
	disabled?: boolean
	onUpdate?: (value: number) => void
	onUpdateEnded?: (value: number) => void
}) => {
	const min = props.input?.min ?? 0
	const max = props.input?.max ?? 100
	const step = props.input?.step ?? 1

	return (
		<Slider
			className="[&_[aria-hidden]]:!w-[84px] [&_[aria-hidden]]:whitespace-nowrap [&_[role=group]>div:last-child]:!w-[4ch]"
			title={props.title}
			value={props.value}
			defaultValue={0}
			min={min}
			max={max}
			step={step}
			transform={noopSliderTransform}
			titleAlignment="right"
			disabled={props.disabled}
			showTickMarks={props.input?.type === "stepped"}
			onUpdate={props.onUpdate}
			onUpdateEnded={(_, displayedValue) => props.onUpdateEnded?.(displayedValue)}
		/>
	)
}
